import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {Box, Grid, CircularProgress, Button} from '@mui/material'
import {Refresh as RefreshIcon, CloudOff as CloudOffIcon} from '@mui/icons-material'
import {useSearchParams, useNavigate} from 'react-router-dom'

import {
  smbsApi,
  SMBData,
  SMBDetailData,
  SMBDashboardSummary,
} from '../../services/api'
import {useNotification} from '../../contexts/NotificationContext'
import {useSMBContext} from '../../contexts/SMBContext'
import SMBSelector from '../../components/SMBSelector'
import UpdateKnowledgeDialog from '../../components/dialogs/UpdateKnowledgeDialog'
import SMBDetailsDrawer from '../../components/dashboard/SMBDetailsDrawer'
import {PlanSelectionGrid, PaymentProcessingModal} from '../../components/payment'
import {usePaymentContext} from '../../contexts/PaymentContext'
import EmptyState from '../../components/EmptyState'
import {PAGE_BANNER_PRIMARY_BTN_SX} from '../../theme/constants'

import { ConnectWhatsAppState } from './components/ConnectWhatsAppState'
import { DashboardHeader } from './components/DashboardHeader'
import { MetricCardsRow } from './components/MetricCardsRow'
import { AIVanityBanner } from './components/AIVanityBanner'
import { ConversationStatsChart } from './components/ConversationStatsChart'
import { LeadSourcesChart } from './components/LeadSourcesChart'
import { UpcomingAppointmentsList } from './components/UpcomingAppointmentsList'

type PageState = 'loading' | 'error_retry' | 'step2_select_plan' | 'step4_connect_meta' | 'dashboard'

const SMBDashboard: React.FC = () => {
  const {showNotification} = useNotification()
  const {smbs, selectedSMBId, setSelectedSMBId, loading: smbsLoading, isError: smbsError, refreshSMBs} = useSMBContext()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [summary, setSummary] = useState<SMBDashboardSummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)

  const {
    plans,
    loading: paymentLoading,
    isError: paymentError,
    hasAnySubscription,
    hasAIAutomation,
    hasPendingCheckout,
    checkoutLoading,
    pollingStatus,
    handleSelectPlan,
    refreshBilling,
  } = usePaymentContext()

  // Derived current SMB from context & selection
  const smb = useMemo(() => {
    return (selectedSMBId ? smbs.find((s) => s.id === selectedSMBId) : smbs[0])
      || smbs[0]
      || null
  }, [selectedSMBId, smbs])

  // Pure derived pageState — ZERO race conditions!
  const pageState = useMemo<PageState>(() => {
    if (smbsLoading || paymentLoading) return 'loading'
    // If backend returns a 500/network error, show fallback refresh screen so user isn't stuck in infinite onboarding retries
    if (smbsError || paymentError) return 'error_retry'
    // If backend returns 200 OK with 0 SMBs or missing ID, route to WhatsApp onboarding journey
    if (!smbs || smbs.length === 0 || !smb?.id) return 'step4_connect_meta'
    const isOnboarded = Boolean(smb.onboarding_completed)

    if (!isOnboarded && !hasAnySubscription) {
      if (checkoutLoading || searchParams.get('plan_type')) return 'loading'
      // Allow bypassing plan selection if user has a pending payment in progress
      if (hasPendingCheckout) return 'step4_connect_meta'
      return plans.length === 0 ? 'step4_connect_meta' : 'step2_select_plan'
    }
    if (!isOnboarded && hasAnySubscription) return 'step4_connect_meta'
    return 'dashboard'
  }, [smbsLoading, paymentLoading, smbsError, paymentError, smbs, smb, hasAnySubscription, hasPendingCheckout, plans.length, checkoutLoading, searchParams])

  // Query Param logic for plan_type
  useEffect(() => {
    if (paymentLoading) return
    const planTypeParam = searchParams.get('plan_type')
    if (planTypeParam) {
      searchParams.delete('plan_type')
      setSearchParams(searchParams, {replace: true})
      if (hasAnySubscription) {
        navigate('/billing')
      } else {
        handleSelectPlan(planTypeParam)
      }
    }
  }, [paymentLoading, searchParams, setSearchParams, hasAnySubscription, navigate, handleSelectPlan])

  const [showKnowledgeDialog, setShowKnowledgeDialog] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [drawerData, setDrawerData] = useState<SMBDetailData | null>(null)
  const [refreshingWebsite, setRefreshingWebsite] = useState(false)
  const [isScraping, setIsScraping] = useState(false)

  // ------------------------------------------------------------------
  // Load summary
  // ------------------------------------------------------------------
  const loadSummary = useCallback(async (smbId: string) => {
    setSummaryLoading(true)
    try {
      const data = await smbsApi.getSMBDashboardSummary(smbId)
      setSummary(data)
    } catch (err) {
      console.error('Failed to load dashboard summary', err)
    } finally {
      setSummaryLoading(false)
    }
  }, [])

  // Load dashboard metrics when on dashboard view
  useEffect(() => {
    if (pageState === 'dashboard' && smb?.id) {
      loadSummary(smb.id)
    }
  }, [pageState, smb?.id, loadSummary])

  // ------------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------------
  const handleMetaCompleted = async () => {
    await refreshSMBs()
    await refreshBilling()
    if (smb?.id) {
      loadSummary(smb.id)
    }
    // Only show knowledge dialog if subscription includes AI automation entitlement
    if (hasAnySubscription && hasAIAutomation) {
      setShowKnowledgeDialog(true)
    }
  }

  const processBackgroundUpdate = (updatedSMB: SMBData, promise?: Promise<any>, isDocument?: boolean) => {
    refreshSMBs()
    if (promise) {
      setIsScraping(true)
      showNotification(
        isDocument
          ? 'Processing document in background...'
          : 'Scraping website & generating AI knowledge in background...',
        'info'
      )

      promise
        .then((res: any) => {
          showNotification(res?.message || 'Knowledge updated successfully!', 'success')
          if (updatedSMB.id) {
            loadSummary(updatedSMB.id)
          }
        })
        .catch((err: any) => {
          console.error('Background update failed', err)
          showNotification('Failed to update business knowledge.', 'error')
        })
        .finally(() => {
          setIsScraping(false)
        })
    } else if (updatedSMB.id) {
      loadSummary(updatedSMB.id)
    }
  }

  const handleKnowledgeFinish = (updatedSMB: SMBData, promise?: Promise<any>, isDocument?: boolean) => {
    setShowKnowledgeDialog(false)
    processBackgroundUpdate(updatedSMB, promise, isDocument)
  }

  const handleViewDetails = async () => {
    if (!smb?.id) return
    setDrawerOpen(true)
    setDrawerLoading(true)
    try {
      const data = await smbsApi.getSMB(smb.id)
      setDrawerData(data)
    } catch {
      showNotification('Failed to load business details', 'error')
    } finally {
      setDrawerLoading(false)
    }
  }

  const handleReloadDrawer = async (smbId: string) => {
    try {
      const data = await smbsApi.getSMB(smbId)
      setDrawerData(data)
    } catch (err) {
      console.error('Failed to reload drawer data', err)
    }
  }

  const handleRefreshWebsite = async (targetSMB: SMBData) => {
    if (!targetSMB?.id || !targetSMB.website) return
    setRefreshingWebsite(true)
    try {
      const response = (await smbsApi.createUpdateSMB({
        smb_id: targetSMB.id,
        refresh_scrape: true,
      })) as any
      const lastRunMessage = response.last_run_at
        ? `Last run ${new Date(response.last_run_at).toLocaleString()} (${response.last_status || 'unknown'})`
        : undefined

      showNotification(
        [response.message || 'Website scraped successfully! ', lastRunMessage].filter(Boolean).join(' — '),
        'success'
      )
      await handleReloadDrawer(targetSMB.id)
    } catch (error: any) {
      const detail = error.response?.data?.error?.message
      if (error.response?.status === 429 && detail) {
        showNotification(detail.message || 'Website scrape recently triggered. Try again later.', 'info')
      } else if (typeof detail === 'string') {
        showNotification(detail, 'error')
      } else if (detail?.message) {
        showNotification(detail.message, 'error')
      } else {
        showNotification('Failed to refresh website data', 'error')
      }
    } finally {
      setRefreshingWebsite(false)
    }
  }

  // ------------------------------------------------------------------
  // Render Journey States (Strict 1:1 State Mapping)
  // ------------------------------------------------------------------

  // 1. Loading State
  if (pageState === 'loading') {
    return (
      <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', gap: 2}}>
        {!checkoutLoading && <CircularProgress />}
        <PaymentProcessingModal
          open={checkoutLoading || Boolean(pollingStatus)}
          status={pollingStatus}
          mode={pollingStatus ? 'processing' : 'redirecting'}
        />
      </Box>
    )
  }

  // 1b. Server / Fetch Error State
  if (pageState === 'error_retry') {
    return (
      <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 3, textAlign: 'center', px: 2}}>
        <EmptyState
          icon={<CloudOffIcon sx={{fontSize: 56, color: 'text.secondary', opacity: 0.8}} />}
          title="Something Went Wrong"
          description="We couldn't load your information right now. Please click refresh to try again."
          action={
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={async () => {
                await refreshSMBs()
                await refreshBilling()
              }}
              sx={PAGE_BANNER_PRIMARY_BTN_SX}
            >
              Refresh Page
            </Button>
          }
        />
      </Box>
    )
  }

  // 2. Step 2: Choose Plan (When not onboarded and no active sub)
  if (pageState === 'step2_select_plan') {
    return (
      <Box sx={{position: 'relative'}}>
        <PaymentProcessingModal
          open={checkoutLoading || Boolean(pollingStatus)}
          status={pollingStatus}
          mode={pollingStatus ? 'processing' : 'redirecting'}
        />
        <PlanSelectionGrid plans={plans} onSelectPlan={handleSelectPlan} loading={checkoutLoading} />
      </Box>
    )
  }

  // 3. Step 4: Connect to Meta (When active sub present OR no plans available)
  if (pageState === 'step4_connect_meta') {
    return <ConnectWhatsAppState smbId={smb?.id} onCompleted={handleMetaCompleted} />
  }

  // 4. Step 7: Full Operational Dashboard
  const SIDEBAR_WIDTH = 150
  const isOverallSummaryLoading = summaryLoading

  return (
    <Box sx={{position: 'relative', minHeight: '100%'}}>

      {/* Knowledge sources dialog after first Meta signup */}
      {smb?.id && showKnowledgeDialog && (
        <UpdateKnowledgeDialog
          mode="setup"
          open={showKnowledgeDialog}
          smb={smb}
          onClose={() => setShowKnowledgeDialog(false)}
          onSkip={() => setShowKnowledgeDialog(false)}
          onSuccess={handleKnowledgeFinish}
          dialogSx={{ '& .MuiDialog-container': { paddingLeft: { xs: 0, md: `${SIDEBAR_WIDTH}px` } } }}
        />
      )}

      {/* Edit dialog */}
      {smb && editOpen && (
        <UpdateKnowledgeDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          smb={smb}
          onSuccess={(updated, promise, isDocument) => {
            setEditOpen(false)
            processBackgroundUpdate(updated, promise, isDocument)
          }}
        />
      )}

      {/* Details drawer */}
      <SMBDetailsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        smb={drawerData}
        loading={drawerLoading}
        onRefreshWebsite={handleRefreshWebsite}
        refreshing={refreshingWebsite}
        scrapeMeta={drawerData?.scrape_metadata}
        onReload={handleReloadDrawer}
      />

      {/* ---- All dashboard content ---- */}
      <Box
        sx={{
          ...(showKnowledgeDialog && {
            filter: 'blur(4px)',
            pointerEvents: 'none',
            userSelect: 'none',
          }),
        }}
      >
        <DashboardHeader
          smb={smb}
          loading={isOverallSummaryLoading}
          isScraping={isScraping}
          summaryLoading={isOverallSummaryLoading}
          handleViewDetails={handleViewDetails}
          setEditOpen={setEditOpen}
          loadSummary={loadSummary}
        />
        <SMBSelector smbs={smbs} selectedSMBId={selectedSMBId} onChange={setSelectedSMBId} />

        <MetricCardsRow summary={summary} summaryLoading={isOverallSummaryLoading} />

        <AIVanityBanner aiMessagesSent={summary?.ai_messages_sent ?? 0} loading={isOverallSummaryLoading && !summary} />

        {/* ---- Charts row ---- */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <ConversationStatsChart summary={summary} summaryLoading={isOverallSummaryLoading} />
          </Grid>

          <Grid item xs={12} md={6}>
            <LeadSourcesChart summary={summary} summaryLoading={isOverallSummaryLoading} />
          </Grid>
        </Grid>

        <UpcomingAppointmentsList summary={summary} summaryLoading={isOverallSummaryLoading} />
      </Box>
    </Box>
  )
}

export default SMBDashboard
